/** Words and phrases shared across the console: buttons, statuses, roles, permissions, errors, tables, dates. */
export const common = {
  cancel: "Cancel",
  save: "Save",
  saving: "Saving…",
  close: "Close",
  done: "Done",
  back: "Back",
  next: "Next",
  continue: "Continue",
  confirm: "Confirm",
  delete: "Delete",
  edit: "Edit",
  remove: "Remove",
  retry: "Try again",
  reload: "Reload",
  copy: "Copy",
  copied: "Copied",
  loading: "Loading",
  loadMore: "Load more",
  showingAll: { one: "Showing {n} result", other: "Showing all {n} results" },
  exportCsv: "Export CSV",
  optional: "optional",
  required: "required",
  yes: "Yes",
  no: "No",
  none: "None",
  never: "Never",
  you: "You",
  all: "All",
  search: "Search",
  clearSearch: "Clear search",
  details: "Details",
  review: "Review",
  learnMore: "Learn more",
  apiDocs: "API docs",

  // Tables and paging
  pagesLabel: "Pages",
  range: "{from}–{to}",
  rangeOf: "{from}–{to} of {total}",
  noRowsOnPage: "No rows on this page",
  prev: "Prev",
  previousPage: "Previous page",
  nextPage: "Next page",
  pageN: "Page {n}",
  rows: "Rows",
  rowsPerPage: "Rows per page",

  // Filters and dates
  status: "Status",
  asset: "Asset",
  allStatuses: "All statuses",
  allAssets: "All assets",
  dateRange: "Date range (UTC days)",
  allTime: "All time",
  today: "Today",
  days7: "7 days",
  days30: "30 days",
  custom: "Custom",
  from: "From",
  to: "To",
  utcDays: "UTC days",
  justNow: "just now",
  ago: "{t} ago",
  inTime: "in {t}",
  minutes: "{n} min",
  hours: "{n} h",
  days: "{n} d",

  // Statuses (API codes → labels); unknown codes fall back to the code itself
  statuses: {
    pending: "Pending",
    pending_approval: "Waiting for approval",
    detected: "Detected",
    confirmed: "Confirmed",
    swept: "Swept",
    below_min: "Below minimum",
    flagged: "Flagged",
    orphaned: "Orphaned",
    requested: "Requested",
    queued: "Queued",
    signing: "Signing",
    broadcast: "Broadcast",
    completed: "Completed",
    failed: "Failed",
    rejected: "Rejected",
    delivered: "Delivered",
    active: "Active",
    revoked: "Revoked",
    disabled: "Disabled",
    enrolling: "Pending setup",
    expired: "Expired",
    paused: "Paused",
  },

  // Built-in roles (custom role names are the merchant's own text and are never translated)
  roles: { owner: "Owner", finance: "Finance", developer: "Developer", viewer: "Viewer" },
  roleDesc: {
    owner: "Everything, including team management",
    finance: "Create and approve payouts, view activity",
    developer: "API keys and webhooks, view activity",
    viewer: "Read-only access to balances, deposits and payouts",
  },

  // Permission matrix: page rows, action columns, special labels and help tooltips
  permPages: {
    Overview: "Overview",
    Deposits: "Deposits",
    Payouts: "Payouts",
    Statement: "Statement",
    "API keys": "API keys",
    Webhook: "Webhook",
    Team: "Team",
    Roles: "Roles",
    Activity: "Activity",
    Settings: "Settings",
    Other: "Other",
  },
  permActions: {
    view: "View",
    export: "Export",
    create: "Create",
    approve: "Approve",
    revoke: "Revoke",
    edit: "Edit",
    invite: "Invite",
    disable: "Disable",
    manage: "Manage",
    reveal: "View API secrets",
  },
  permConsoleIp: "Edit console IP allowlist",
  permHelp: {
    "overview:view": "Balances and today's totals",
    "deposits:view": "Deposit list and details",
    "deposits:export": "Download deposits as CSV",
    "payouts:view": "Payout list, details and fee quotes",
    "payouts:create": "Create payouts; create API keys that can send payouts",
    "payouts:approve": "Approve or reject payouts waiting for approval",
    "payouts:export": "Download payouts as CSV",
    "statement:view": "Balance history and its CSV",
    "api_keys:view": "See API keys",
    "api_keys:create": "Create API keys",
    "api_keys:edit": "Change the server IPs of existing API keys",
    "api_keys:revoke": "Revoke API keys",
    "api_keys:reveal": "View an API key's secret again (2FA every time, logged, Owners notified)",
    "webhook:view": "Webhook endpoint and deliveries",
    "webhook:edit": "Change the webhook URL, resend events",
    "team:view": "Team page and roles list",
    "team:invite": "Invite people, cancel invites",
    "team:edit": "Change someone's role and brands",
    "team:disable": "Disable team members",
    "roles:manage": "Create, change and delete custom roles",
    "activity:view": "Activity log",
    "settings:view": "Settings page",
    "console_ip:edit": "Change the console IP allowlist (organization and per person)",
  },

  // Errors: specific codes are translated; generic ones prefix the server's (English) detail
  errors: {
    generic: "Something went wrong. Please try again.",
    network: "Can't reach the server. Check your connection and try again.",
    unauthenticated: "Your session has ended. Please log in again.",
    mfa_required: "That code is wrong or expired. Enter the current 6-digit code from your authenticator app.",
    invalid_credentials: "Email or password is incorrect.",
    rate_limited: "Too many attempts. Please wait a few minutes and try again.",
    forbidden: "Your role doesn't allow this action.",
    ip_not_allowed: "Access from this network is not allowed.",
    no_access: "Your account has no active brand. Ask your account owner or iCryptoNow support.",
    login_required: "You already have an iCryptoNow login. Log in first, then open the invite link again.",
    csrf: "The request was blocked by a security check. Reload the page and try again.",
    not_found: "Not found.",
    conflict: "This conflicts with the current state. Reload and try again.",
    bad_request: "This request can't be processed.",
    insufficient_balance: "Not enough balance for this payout.",
    payouts_paused: "Payouts are paused for this account.",
    /** Generic code + the server's English detail, for languages other than English. */
    withDetail: "{message} ({detail})",
  },
};
